/**
 * Route utilities for WillPower Store navigation
 * Provides type-safe routing with query parameters for filtering
 */

// Default/fallback categories - used for type safety and fallbacks
export const DEFAULT_PRODUCT_CATEGORIES = [
  "protein",
  "pre-workout",
  "vitamins",
  "creatine",
  "fat-burners",
  "recovery",
] as const;

// Default/fallback brands - used for type safety and fallbacks
export const DEFAULT_BRANDS = [
  "optimum-nutrition",
  "myprotein",
  "grenade",
  "bulk",
  "usn",
  "applied-nutrition",
] as const;

// Legacy exports for backward compatibility
export const PRODUCT_CATEGORIES = DEFAULT_PRODUCT_CATEGORIES;
export const BRANDS = DEFAULT_BRANDS;

export const SORT_OPTIONS = [
  "featured",
  "price-low-high",
  "price-high-low",
  "newest",
  "best-selling",
  "alphabetical",
] as const;

// Base types for static type checking
export type DefaultProductCategory =
  (typeof DEFAULT_PRODUCT_CATEGORIES)[number];
export type DefaultBrand = (typeof DEFAULT_BRANDS)[number];
export type SortOption = (typeof SORT_OPTIONS)[number];

// Flexible types that support both static and dynamic values
export type ProductCategory = DefaultProductCategory | string;
export type Brand = DefaultBrand | string;

// Legacy type exports for backward compatibility
export type LegacyProductCategory = DefaultProductCategory;
export type LegacyBrand = DefaultBrand;

export type ShopFilters = {
  categories?: ProductCategory[];
  brands?: Brand[];
  sort?: SortOption;
  minPrice?: number;
  maxPrice?: number;
  search?: string;
};

/**
 * @deprecated Use nuqs-based useShopFilters hook instead
 * Legacy URL generation functions - kept for backward compatibility
 */

/**
 * Validate category parameter (checks against default categories)
 */
export function isValidCategory(
  category: string
): category is DefaultProductCategory {
  return DEFAULT_PRODUCT_CATEGORIES.includes(
    category as DefaultProductCategory
  );
}

/**
 * Validate brand parameter (checks against default brands)
 */
export function isValidBrand(brand: string): brand is DefaultBrand {
  return DEFAULT_BRANDS.includes(brand as DefaultBrand);
}

/**
 * Validate sort option parameter
 */
export function isValidSortOption(sort: string): sort is SortOption {
  return SORT_OPTIONS.includes(sort as SortOption);
}

/**
 * Check if category is a known default category
 */
export function isDefaultCategory(
  category: string
): category is DefaultProductCategory {
  return isValidCategory(category);
}

/**
 * Check if brand is a known default brand
 */
export function isDefaultBrand(brand: string): brand is DefaultBrand {
  return isValidBrand(brand);
}

/**
 * Get category display name
 */
export function getCategoryDisplayName(category: ProductCategory): string {
  const displayNames: Record<string, string> = {
    // Static/fallback categories
    protein: "Protein",
    "pre-workout": "Pre-Workout",
    vitamins: "Vitamins",
    creatine: "Creatine",
    "fat-burners": "Fat Burners",
    recovery: "Recovery",

    // Dynamic Shopify categories
    performance: "Performance",
    supplements: "Supplements",

    // Additional potential categories
    "mass-gainers": "Mass Gainers",
    "weight-loss": "Weight Loss",
    amino: "Amino Acids",
    bcaa: "BCAA",
    glutamine: "Glutamine",
    "post-workout": "Post-Workout",
    energy: "Energy",
    health: "Health",
    nutrition: "Nutrition",
    fitness: "Fitness",
    bodybuilding: "Bodybuilding",
    endurance: "Endurance",
    hydration: "Hydration",
    "joint-support": "Joint Support",
    "immune-support": "Immune Support",
    "sleep-support": "Sleep Support",
    "digestive-health": "Digestive Health",

    // Production categories (from screenshot)
    "bcaa-eaa": "Bcaa & Eaa",
    "creatine-pre-workout": "Creatine & Pre Workout",
    mass: "Mass",
    "vegan-protein": "Vegan Protein",
  };

  // Return specific display name if available, otherwise format the category
  if (displayNames[category]) {
    return displayNames[category];
  }

  // Fallback: Convert kebab-case to Title Case
  return category
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * Get category description
 */
export function getCategoryDescription(category: ProductCategory): string {
  const descriptions: Record<string, string> = {
    // Static/fallback categories
    protein: "Whey, casein, and plant-based protein powders",
    "pre-workout": "Energy and focus supplements for training",
    vitamins: "Essential vitamins and minerals",
    creatine: "Pure creatine monohydrate and blends",
    "fat-burners": "Thermogenic and metabolism support",
    recovery: "Post-workout recovery supplements",

    // Dynamic Shopify categories
    performance: "Strength and endurance enhancing supplements",
    supplements: "General health and wellness supplements",

    // Additional potential categories
    "mass-gainers": "Weight gain and muscle building supplements",
    "weight-loss": "Fat burning and weight management supplements",
    amino: "Essential and branched-chain amino acids",
    bcaa: "Branched-chain amino acid supplements",
    glutamine: "Recovery and immune support supplements",
    "post-workout": "Recovery and muscle repair supplements",
    energy: "Natural energy and focus supplements",
    health: "General health and wellness products",
    nutrition: "Nutritional supplements and vitamins",
    fitness: "Fitness and performance supplements",
    bodybuilding: "Muscle building and strength supplements",
    endurance: "Stamina and endurance supplements",
    hydration: "Electrolyte and hydration supplements",
    "joint-support": "Joint health and mobility supplements",
    "immune-support": "Immune system boosting supplements",
    "sleep-support": "Natural sleep and recovery aids",
    "digestive-health": "Digestive enzymes and gut health supplements",
  };

  // Return specific description if available, otherwise generate a fallback
  if (descriptions[category]) {
    return descriptions[category];
  }

  // Fallback: Generate a professional description for unknown categories
  const displayName = getCategoryDisplayName(category);
  return `High-quality ${displayName.toLowerCase()} supplements and products`;
}

/**
 * Get sort option display name
 */
export function getSortDisplayName(sort: SortOption): string {
  const displayNames: Record<SortOption, string> = {
    featured: "Featured",
    "price-low-high": "Price: Low to High",
    "price-high-low": "Price: High to Low",
    newest: "Newest",
    "best-selling": "Best Selling",
    alphabetical: "Alphabetical",
  };

  return displayNames[sort];
}

/**
 * Static route definitions
 */
export const routes = {
  // Main pages
  home: "/",
  shop: "/shop",
  contact: "/contact",
  about: "/about",

  // Product pages
  products: "/products",
  basket: "/basket",
  // Checkout routes - Shopify handles checkout, these are for return/success pages
  checkout: "/checkout",

  // Authentication routes
  auth: {
    login: "/auth/login",
    register: "/auth/register",
    logout: "/auth/logout",
    forgotPassword: "/auth/forgot-password",
    resetPassword: "/auth/reset-password",
  },

  // Protected user pages
  account: "/account",
  settings: "/settings",

  // Account sub-pages
  accountPayment: "/account/payment",
  accountOrders: "/account/orders",

  // Error pages
  unauthorized: "/unauthorized",
  notFound: "/404",

  // Support pages
  help: "/help",
  faq: "/faq",
  privacy: "/privacy-policy",
  terms: "/terms-of-service",
  cookies: "/cookie-policy",
  returns: "/returns",
  shipping: "/shipping",
} as const;

// Helper functions for URL building
export function product(handle: string): string {
  return `${routes.products}/${handle}`;
}

export function orderDetail(orderId: string): string {
  return `${routes.accountOrders}/${orderId}`;
}

export function buildLoginUrl(callbackUrl?: string) {
  return callbackUrl
    ? `${routes.auth.login}?callbackUrl=${encodeURIComponent(callbackUrl)}`
    : routes.auth.login;
}

export function buildRegisterUrl(callbackUrl?: string) {
  return callbackUrl
    ? `${routes.auth.register}?callbackUrl=${encodeURIComponent(callbackUrl)}`
    : routes.auth.register;
}

export type AccountTab = "profile" | "wishlist" | "orders" | "payment";

/**
 * Builds a URL to the account page, optionally targeting a specific tab.
 * @param tab The account tab to link to.
 * @returns A URL string for the account page.
 */
export function buildAccountUrl(tab?: AccountTab): string {
  if (tab !== undefined) {
    return `${routes.account}?tab=${tab}`;
  }
  return routes.account;
}

// Protected routes array for middleware
export const PROTECTED_ROUTES = [routes.account, routes.settings] as const;

// Navigation link groups
export const NAV_LINKS = {
  support: [
    { title: "Help Center", href: routes.help },
    { title: "FAQ", href: routes.faq },
    { title: "Shipping Info", href: routes.shipping },
    { title: "Returns", href: routes.returns },
  ],

  account: [
    { title: "My Account", href: buildAccountUrl("profile") },
    { title: "Order History", href: buildAccountUrl("orders") },
    { title: "Wishlist", href: buildAccountUrl("wishlist") },
    { title: "Account Settings", href: routes.settings },
  ],
} as const;

/**
 * Hook to get display names for categories
 */
export function useCategoryDisplayNames() {
  const displayNameMap: Record<string, string> = {
    // Development categories
    protein: "Protein",
    "pre-workout": "Pre-Workout",
    vitamins: "Vitamins",
    creatine: "Creatine",
    "fat-burners": "Fat Burners",
    recovery: "Recovery",
    performance: "Performance",
    supplements: "Supplements",

    // Production categories (from screenshot)
    "bcaa-eaa": "Bcaa & Eaa",
    "creatine-pre-workout": "Creatine & Pre Workout",
    mass: "Mass",
    "vegan-protein": "Vegan Protein",
  };

  return (category: string) =>
    displayNameMap[category] ||
    category.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
}

/**
 * Hook to get display names for brands
 */
export function useBrandDisplayNames() {
  const displayNameMap: Record<string, string> = {
    "optimum-nutrition": "Optimum Nutrition",
    myprotein: "Myprotein",
    grenade: "Grenade",
    bulk: "Bulk",
    usn: "USN",
    "applied-nutrition": "Applied Nutrition",
  };

  return (brand: string) =>
    displayNameMap[brand] ||
    brand.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
}
